import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { Sparkles, ArrowRight, Wand2 } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import client from "@/lib/api";
import { toast } from "sonner";

const iconFor = (name) => {
  const key = (name || "").split("-").map((s) => s[0]?.toUpperCase() + s.slice(1)).join("");
  return Icons[key] || Icons.LayoutTemplate;
};

const SUGGESTIONS = [
  "a booking page for my barbershop",
  "a portfolio for a wedding photographer",
  "a waitlist for my new app",
  "a one-page menu for my cafe",
];

export default function AppTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [building, setBuilding] = useState(false);

  useEffect(() => {
    client.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
  }, []);

  const create = async ({ template, promptText }) => {
    setBuilding(true);
    try {
      const { data } = await client.post("/projects", {
        template: template?.id,
        prompt: promptText || undefined,
        name: template?.name,
      });
      navigate(`/app/build/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't build that. Try again.");
      setBuilding(false);
    }
  };

  return (
    <AppShell title="Create">
      <p className="text-ho-muted">Describe an idea, or start from a template.</p>

      {/* Prompt box */}
      <div className="mt-5 rounded-[28px] border border-black/5 bg-white p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-ho-primary">
          <Wand2 className="w-4 h-4" /> Build with AI
        </div>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          placeholder="e.g. a landing page for my dog-walking business with pricing and a contact form"
          data-testid="create-prompt-input"
          className="mt-3 w-full resize-none rounded-2xl bg-ho-bg px-4 py-3 text-[15px] outline-none"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => setPrompt(s)} data-testid="prompt-suggestion"
              className="tap-scale rounded-full bg-black/5 px-3 py-1.5 text-xs font-medium text-ho-ink/70">
              {s}
            </button>
          ))}
        </div>
        <button
          onClick={() => create({ promptText: prompt })}
          disabled={!prompt.trim() || building}
          data-testid="create-build-button"
          className="tap-scale mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ho-primary py-3.5 font-semibold text-white disabled:opacity-50"
        >
          <Sparkles className="w-4 h-4" /> Build it
        </button>
      </div>

      {/* Templates */}
      <h2 className="font-display mt-8 mb-3 text-lg font-bold">Templates</h2>
      <div className="grid grid-cols-2 gap-3">
        {templates.map((t, i) => {
          const Icon = iconFor(t.icon);
          return (
            <motion.button key={t.id}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              onClick={() => create({ template: t })}
              disabled={building}
              data-testid={`app-template-${t.id}`}
              className="tap-scale overflow-hidden rounded-3xl border border-black/5 bg-white text-left disabled:opacity-60">
              <div className="relative h-24 overflow-hidden">
                <img src={t.cover} alt={t.name} className="h-full w-full object-cover" />
                <div className="absolute left-2 top-2 grid place-items-center w-8 h-8 rounded-xl bg-white/90 backdrop-blur">
                  <Icon className="w-4 h-4 text-ho-primary" />
                </div>
              </div>
              <div className="p-3">
                <div className="font-display text-[15px] font-bold">{t.name}</div>
                <div className="text-xs text-ho-muted">{t.tagline}</div>
              </div>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence>
        {building && <BuildingOverlay />}
      </AnimatePresence>
    </AppShell>
  );
}

const BuildingOverlay = () => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[60] grid place-items-center bg-ho-bg/80 backdrop-blur-xl">
    <div className="flex flex-col items-center gap-4 px-8 text-center">
      <div className="relative grid place-items-center w-20 h-20 rounded-[28px] bg-ho-primary/10">
        <Sparkles className="w-9 h-9 text-ho-primary" />
        <div className="absolute inset-0 rounded-[28px] border-2 border-ho-primary/30 animate-ping" />
      </div>
      <div>
        <div className="font-display text-xl font-bold">Building your site…</div>
        <p className="mt-1 text-ho-muted">The AI is designing and writing it now.</p>
      </div>
    </div>
  </motion.div>
);
